import { SERVICES_DATA, getServiceBySlug } from "@/lib/services-data";

export const SITE_URL = "https://www.himilostudio.com";
export const SITE_NAME = "Himilo Studio";

export interface PageMeta {
  title: string;
  description: string;
  noindex?: boolean;
}

/** Titles/descriptions for the fixed public routes. */
const STATIC_META: Record<string, PageMeta> = {
  "/": {
    title: "Himilo Studio | Luxury Cinematic Videography",
    description:
      "Himilo Studio crafts luxury cinematic videography for weddings, brands, events and more — visual stories that captivate and inspire.",
  },
  "/home": {
    title: "Himilo Studio | Luxury Cinematic Videography",
    description:
      "Himilo Studio crafts luxury cinematic videography for weddings, brands, events and more — visual stories that captivate and inspire.",
  },
  "/landing": {
    title: "Himilo Studio | Luxury Cinematic Videography",
    description:
      "Himilo Studio crafts luxury cinematic videography for weddings, brands, events and more — visual stories that captivate and inspire.",
  },
  "/about": {
    title: "About Himilo Studio | Our Story & Filmmaking Process",
    description:
      "Meet the team behind Himilo Studio and see how our discovery, consultation, production and delivery process turns your day into a cinematic film.",
  },
  "/portfolio": {
    title: "Portfolio & Photo Gallery | Himilo Studio",
    description:
      "Browse showreels, wedding films, brand campaigns and event highlights, plus our photo gallery of recent Himilo Studio shoots across the UK.",
  },
  "/services": {
    title: "Videography Services & Packages | Himilo Studio",
    description:
      "Explore Himilo Studio videography services — weddings, events, brands, property, drone, live streaming and social media — with packages and pricing.",
  },
  "/contact": {
    title: "Contact Himilo Studio | Enquiries & Bookings",
    description:
      "Get in touch with Himilo Studio by WhatsApp, phone or email to check availability, request a quote or book a consultation for your project.",
  },
  "/auth": {
    title: "Sign In | Himilo Studio Client Portal",
    description: "Sign in to the Himilo Studio client portal to view bookings, invoices, contracts and delivered media.",
    noindex: true,
  },
  "/reschedule-consultation": {
    title: "Reschedule Your Consultation | Himilo Studio",
    description: "Pick a new date and time for your Himilo Studio consultation.",
    noindex: true,
  },
};

const SERVICE_META: Record<string, PageMeta> = Object.fromEntries(
  SERVICES_DATA.map((s) => [
    s.href,
    {
      title: `${s.title} Videography | Himilo Studio`,
      description: s.description,
    } as PageMeta,
  ]),
);

const titleCaseSlug = (slug: string) =>
  slug
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

/** Resolve the metadata for a pathname. Always returns a self-referencing canonical. */
export function getPageMeta(pathname: string): PageMeta & { canonical: string } {
  const path = pathname !== "/" && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  const canonical = `${SITE_URL}${path === "/" ? "/" : path}`;

  const isPrivate = /^\/(dashboard|admin|\.lovable)/.test(path);
  if (isPrivate) {
    return {
      title: "Client Portal | Himilo Studio",
      description: "Secure Himilo Studio portal for managing bookings, projects, documents and media.",
      noindex: true,
      canonical,
    };
  }

  const match = STATIC_META[path] ?? SERVICE_META[path];
  if (match) return { ...match, canonical };

  const serviceSlug = path.startsWith("/services/") ? path.replace("/services/", "") : null;
  if (serviceSlug) {
    const known = getServiceBySlug(serviceSlug);
    const name = known?.title ?? titleCaseSlug(serviceSlug);
    return {
      title: `${name} Videography | Himilo Studio`,
      description:
        known?.description ??
        `${name} videography by Himilo Studio — cinematic filming, editing and delivery tailored to your ${name.toLowerCase()} project.`,
      canonical,
    };
  }

  return {
    title: "Page Not Found | Himilo Studio",
    description: "The page you are looking for could not be found. Explore Himilo Studio videography services instead.",
    noindex: true,
    canonical,
  };
}
